import React, { useEffect, useState, useMemo } from 'react';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Loader2, CheckCircle, XCircle, Clock, AlertTriangle, Download,
  Search, DollarSign, Users, FileCheck
} from 'lucide-react';
import { toast } from 'sonner';

function WaitBadge({ hours }) {
  if (hours >= 24) return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-600">{Math.floor(hours)}h waiting</span>;
  if (hours >= 12) return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">{Math.floor(hours)}h waiting</span>;
  return <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{Math.floor(hours)}h ago</span>;
}

export default function PayrollApprovals() {
  const [tab, setTab] = useState('pending');
  const [data, setData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const loadPending = async () => {
    try { setData(await superAdminAPI.getApprovalQueue('pending_approval')); }
    catch { toast.error('Failed to load approvals'); }
    finally { setLoading(false); }
  };

  const loadHistory = async () => {
    try { setHistoryData(await superAdminAPI.getApprovalQueue('approved')); }
    catch { toast.error('Failed to load history'); }
  };

  useEffect(() => { loadPending(); }, []);
  useEffect(() => { if (tab === 'history' && !historyData) loadHistory(); }, [tab]);

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await superAdminAPI.approvePayroll(id);
      toast.success('Payroll approved');
      loadPending();
    } catch (err) { toast.error(err.response?.data?.detail || 'Approval failed'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async () => {
    if (rejectReason.length < 20) { toast.error('Reason must be at least 20 characters'); return; }
    setActionLoading(rejectModal);
    try {
      await superAdminAPI.rejectPayroll(rejectModal, rejectReason);
      toast.success('Payroll rejected');
      setRejectModal(null);
      setRejectReason('');
      loadPending();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rejection failed'); }
    finally { setActionLoading(null); }
  };

  const filteredPending = useMemo(() => {
    const items = data?.payrolls || [];
    if (!search) return items;
    return items.filter(p => p.company_name.toLowerCase().includes(search.toLowerCase()));
  }, [data, search]);

  const filteredHistory = useMemo(() => {
    const items = historyData?.payrolls || [];
    if (!search) return items;
    return items.filter(p => p.company_name.toLowerCase().includes(search.toLowerCase()));
  }, [historyData, search]);

  const exportCSV = () => {
    const items = tab === 'pending' ? filteredPending : filteredHistory;
    if (!items.length) return;
    const headers = ['Company', 'Period', 'Amount', 'Recipients', 'Initiator', 'Status', 'Waiting Hours', 'Created At'];
    const rows = items.map(p => [p.company_name, p.period_label, p.total_net, p.employee_count, p.initiator_name, p.status, p.waiting_hours, p.created_at]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `payroll-approvals-${tab}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); toast.success('CSV exported');
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  const summary = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="payroll-approvals-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Payroll Approvals</h1>
          <p className="text-sm text-gray-500">Global approval queue across all companies</p>
        </div>
        <Button variant="outline" onClick={exportCSV} className="h-9 text-xs" data-testid="export-approvals-csv">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Pending Approvals', value: summary.pending_count || 0, icon: Clock, color: 'amber' },
          { label: 'Overdue (24h+)', value: summary.overdue_count || 0, icon: AlertTriangle, color: 'red' },
          { label: 'Total Amount Pending', value: `NGN ${Number(summary.total_pending_amount || 0).toLocaleString()}`, icon: DollarSign, color: 'blue' },
          { label: 'Companies', value: new Set((data?.payrolls || []).map(p => p.company_id)).size, icon: Users, color: 'purple' },
        ].map(s => (
          <Card key={s.label} className="border-gray-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-${s.color}-50 flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 text-${s.color}-500`} />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs + Search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg">
          {[{ id: 'pending', label: 'Pending' }, { id: 'history', label: 'History' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`approval-tab-${t.id}`}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by company..."
            className="h-9 pl-9 text-sm" data-testid="search-approvals" />
        </div>
      </div>

      {/* Pending Tab */}
      {tab === 'pending' && (
        <div className="space-y-3">
          {filteredPending.length === 0 ? (
            <Card className="border-gray-200"><CardContent className="p-12 text-center">
              <FileCheck className="w-10 h-10 text-emerald-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No payrolls pending approval</p>
            </CardContent></Card>
          ) : filteredPending.map(p => (
            <Card key={p.id} className={`border-gray-200 ${p.is_overdue ? 'border-l-4 border-l-red-400' : ''}`} data-testid={`approval-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-bold text-gray-900">{p.company_name}</h3>
                      <WaitBadge hours={p.waiting_hours} />
                    </div>
                    <p className="text-xs text-gray-500">
                      {p.period_label} &middot; Initiated by <span className="font-medium text-gray-700">{p.initiator_name}</span>
                      &middot; {new Date(p.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">NGN {Number(p.total_net).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{p.employee_count} recipients</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${p.sufficient_funds ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                      {p.sufficient_funds ? 'Funds sufficient' : `Insufficient (NGN ${Number(p.wallet_balance).toLocaleString()})`}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setRejectModal(p.id); setRejectReason(''); }}
                      className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-${p.id}`}
                      disabled={!!actionLoading}>
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                    <Button size="sm" onClick={() => handleApprove(p.id)}
                      className="h-8 text-xs bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`approve-${p.id}`}
                      disabled={!!actionLoading}>
                      {actionLoading === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve</>}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* History Tab */}
      {tab === 'history' && (
        <div className="space-y-2">
          {(filteredHistory || []).length === 0 ? (
            <Card className="border-gray-200"><CardContent className="p-8 text-center text-sm text-gray-400">No approval history yet</CardContent></Card>
          ) : filteredHistory.map(p => (
            <Card key={p.id} className="border-gray-200" data-testid={`history-${p.id}`}>
              <CardContent className="p-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{p.company_name} &middot; {p.period_label}</p>
                  <p className="text-xs text-gray-500">NGN {Number(p.total_net).toLocaleString()} &middot; {p.employee_count} recipients</p>
                </div>
                <div className="text-right shrink-0">
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    p.status === 'rejected' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'
                  }`}>{p.status}</span>
                  {p.approved_at && <p className="text-[10px] text-gray-400 mt-1">{new Date(p.approved_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      <Dialog open={!!rejectModal} onOpenChange={() => setRejectModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-base">Reject Payroll</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-gray-500 font-medium mb-1 block">Reason (min 20 characters)</label>
              <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                className="w-full h-24 px-3 py-2 text-sm border rounded-lg resize-none focus:ring-2 focus:ring-pink-200 focus:border-pink-400"
                placeholder="Explain why this payroll is being rejected..." data-testid="reject-reason-input" />
              <p className="text-[10px] text-gray-400 mt-1">{rejectReason.length}/20 minimum</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setRejectModal(null)}>Cancel</Button>
              <Button onClick={handleReject} disabled={rejectReason.length < 20 || !!actionLoading}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-reject-btn">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reject Payroll'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
